(ns pfo.coltype.components.details
  (:require [reagent.core :as reagent :refer [atom track!] :refer-macros [with-let]]
            [pfo.coltype.model :as model :refer [has-nested-value get-value field-column-padding visible-attrs visible-and-not-lonely-attrs]]
            [pfo.coltype.components.common :refer [display-when-cta-warning-applicable cta-missing-fields-warning-text document-field-type show-image-preview]]
            [promesa.core :as p]
            [taoensso.timbre :as timbre
             :refer [log trace debug info warn error fatal report logf
                     tracef debugf infof warnf errorf fatalf reportf spy]]
            [clojure.string :refer [trim]]
            [clojure.set]
            [decimal.core :as dc]
            [goog.string :as gstring]
            [pfo.auth.model :as auth-model]
            [pfo.controller :as controller :refer [subscribe]]
            [pfo.util :as util]
            [pfo.infobox :as infobox]
            [pfo.api :as api :refer [process-error]]
            [pfo.state :as state]
            [pfo.revroutes :refer [path-for]]
            [pfo.common.model :refer [document-complex-name]]
            [pfo.widget.image :refer [image-view]]
            [pfo.document.component :refer [is-image document-thumbnail]]
            [pfo.collection.model :as collection-model :refer [collection-full-details]]))

(declare form-segment form-field field-types string-field skip-fields hide-labels multi-choice-with-other-field field-choices
  attr-to-str field-column-width has-cta-attribute field-to-value-fn)

(def enable-filter-condition-logging false)

(defn filter-fields [fields collection]
  (filterv
    (fn [field]
      (let [debug (fn [msg val] (if enable-filter-condition-logging
                                  (spy :debug (str (util/bracketed (:ref field)) " filter: " msg) val)
                                  val))
            ;; A field might have a :collection key, assigned in cases where it's a linked entiy's field.
            ;; The :collection key is that linked entity, allowing to correctly present the data
            collection (model/field-collection field collection)]
        (and
          (debug "Visibility"
            (model/is-visible
              field
              (fn [aref]
                (get-in collection [:data (keyword aref)]))
              :details))
          (debug "UI field"
            (or (clojure.string/starts-with? (:ref field) "ui_label_") (not (clojure.string/starts-with? (:ref field) "ui_"))))
          ;; Either composite or has value for its ref
          (debug "Composite field with any value"
            (or
              ;; For composite fields, only show those which have at least one non-empty subfield value
              (debug "Composite?"
                (and
                  (debug "Is composite?" (:is_composite field))
                  (debug "Has nested value?" (has-nested-value field collection))))
              (debug "Has value?"
                (util/pytrue? (get-value field collection)))
              (debug "Label field, any of for_fields has value"
                (and
                  (= "label" (:type field))
                  (boolean (seq (filter (fn [field-ref] (not-empty  (get-in collection [:data (keyword field-ref)]))) (:for_fields field))))))))
          ;; Skip choice fields that have no valid choices
          (or
            (debug "Not a choice field"
              (not= (keyword (:type field)) :multi_choice))
            (debug "Field choices empty"
              (not (seq (field-choices field))))
            (debug "Field choices contain field value"
              (some (fn [value] (contains? (field-choices field) value)) (get-value field collection))))
          ;; Ignore types in skip-fields
          (debug "Field not skipped"
            (not (contains? skip-fields (keyword (:type field)))))))) fields))

(defn filter-form-data [segments collection]
  (filterv #(seq (:filtered-fields %)) (mapv #(assoc % :filtered-fields (filter-fields (:fields %) collection)) segments)))

(defn- attribute-column-count [fields ref-field-map collection]
  (into {}
    (map vec
      (partition 2 2
        (flatten
          (map (fn [field]
                 (let [cnt (+
                             (count (model/get-attributes-definition field ref-field-map collection :details))
                             (reduce + (map (fn [subfield] (count (model/get-attributes-definition subfield ref-field-map collection :details))) (:subfields field)))
                             (if (model/has-percentage field ref-field-map collection) 1 0))]
                   [(:ref field) cnt
                    (for [subfield (:subfields field)]
                      [(:ref subfield) cnt])
                    (for [unpacked-field (model/unpack-fields [field] ref-field-map)]
                      [(:ref unpacked-field) cnt])]))
            fields))))))

(defn entity-map-field [field collection ref-field-map entity-id-ref-field-map entity-values]
  (let [entity-field-ref (model/entity-field-ref field)
        entity-attr-ref (not-empty (model/entity-attr-ref field))
        entity-source-refs (model/entity-source-field-refs field)
        entity-id-fn #(model/get-value (ref-field-map entity-field-ref) collection)
        original-field field
        is-entity-linked (and
                           (model/has-entity-link-config field)
                           (contains? ref-field-map entity-field-ref)
                           (entity-id-fn))
        collection (if is-entity-linked
                     (if entity-values
                       (let [owner-id (keyword
                                        (str
                                          (:owner_id (model/get-ownership-info entity-field-ref collection))))
                             entity-ref-field-map (entity-id-ref-field-map (entity-id-fn))
                             entity (entity-values (entity-id-fn))]
                         (if entity-attr-ref
                           (assoc
                             entity
                             :data
                             (merge
                               (:data entity)
                               (get
                                 (model/get-attributes (some entity-ref-field-map entity-source-refs) entity)
                                 owner-id)))
                           entity))
                       nil)
                     collection)
        field (if (and is-entity-linked entity-values)
                (let [entity-ref-field-map (entity-id-ref-field-map (entity-id-fn))]
                  (if entity-attr-ref
                    ((util/associate-by :ref (:attributes (some entity-ref-field-map entity-source-refs)))
                     entity-attr-ref)
                    (some entity-ref-field-map entity-source-refs)))
                field)]
    (if (and field collection)
      (assoc field :collection-fn (fn [] collection))
      original-field)))

(defn entity-map-form-data [segments collection ref-field-map entity-id-ref-field-map entity-values]
  (map
    (fn [segment]
      (merge segment
        {:fields (:subfields
                  (model/map-field
                    (fn [field] (entity-map-field field collection ref-field-map entity-id-ref-field-map entity-values))
                    {:subfields (:fields segment)}))}))
  segments))

(defn collection-coltype-details [options collection]
  (let [collection-type-form-def (subscribe :collection-type-form-def)
        entity-values (atom nil)
        entity-id-fields-map (atom nil)]
    (when-not (:skip-loading-form-def options)
      (controller/dispatch :collection-type-form-def :clear)
      (controller/dispatch :collection-type-form-def :load (:collection_type_id collection)))
    

    (fn [{:keys [first-segment-header-extra-content]
          :as options} collection]
      [:div.o-margin-elements--lg-vrtcl.c-coltype-details
       (when-let [form-def (if-let [form-def-atom (:form-def options)]
                             @form-def-atom
                             @collection-type-form-def)]
         (let [;; all-segment-fields is used for detection of missing CTA fields.
               ;; They are filtered by visibility -- if dependency visibility condition hides field, it musn't be checked.
               all-segment-fields (filter
                                    #(model/is-visible-by-dependency % (fn [aref] (get-in collection [:data (keyword aref)])))
                                    (apply concat (map :fields (:forms form-def))))
               form-fields (model/flatten-fields all-segment-fields)
               ref-field-map (util/associate-by :ref form-fields)
               entity-mapped-segments (entity-map-form-data (:forms form-def) collection ref-field-map @entity-id-fields-map @entity-values)
               filtered-segments (filter-form-data entity-mapped-segments collection)
               attribute-column-counts (reduce merge
                                         (map #(attribute-column-count (:filtered-fields %) ref-field-map collection)
                                           filtered-segments))
               max-attribute-column-count (apply max
                                                 (vals
                                                  attribute-column-counts))
               cta-applies? (fn [type field]
                              ((case type
                                 :highlight model/cta-highlight?
                                 :warning model/cta-warning?)
                               field
                               (fn [ref] (model/get-value (ref-field-map ref) collection))
                               (fn [ref value] ((field-to-value-fn (ref-field-map ref)) value))))
               entity-fields (model/get-entity-fields ref-field-map)
               entity-ids (filterv identity
                            (map
                              (fn [fld] (model/get-value fld collection))
                              entity-fields))]
           (when (and (seq entity-ids)
                   (not @entity-values))
             (model/load-entities
               {:get-values-for-fields-fn (fn [collection _] collection)
                :collection-id-value-map-cb (fn [cvmap]  (swap! entity-values merge cvmap))
                :collection-id-fields-cb (fn [fmap] (swap! entity-id-fields-map merge fmap))}
               entity-ids))
           [:div
            (let [blank-cta-fields (remove
                                    #(has-nested-value % collection)
                                    (filter #(and (cta-applies? :warning %) (not (= "label" (:type %)))) all-segment-fields) )
                  has-blank-cta-attrs (boolean
                                        (some identity
                                          (map
                                            (fn [fld]
                                              (let [selection-count (count (get-value fld collection))
                                                    cta-attr-refs (set (map (comp keyword :ref) (model/field-cta-attributes fld ref-field-map :details)))
                                                    valid-attr-value-count (count
                                                                             (filter
                                                                               (fn [attr-vals]
                                                                                 (not (some nil? attr-vals)))
                                                                               (map
                                                                                 (fn [attrs]
                                                                                   (if (seq cta-attr-refs)
                                                                                     ((apply juxt cta-attr-refs) attrs)
                                                                                     []))
                                                                                 (vals
                                                                                   ;; Get only attributes corresponding to selected choices.
                                                                                   ;; Safety measure -- should come consitent from the back-end.
                                                                                   (select-keys
                                                                                     (model/get-attributes fld collection)
                                                                                     ;; Keyword for each selected ID in the field
                                                                                     (map (comp keyword str) (get-value fld collection)))))))]
                                                (< valid-attr-value-count selection-count)))
                                            (model/fields-with-cta-attrs form-fields))))
                  is-trust-with-no-interest (and
                                              (collection-model/is-trust-collection collection)
                                              (not (nil? (ref-field-map "has_interest")))
                                              ;; The field has inverse meaning in the UI:
                                              (get-value (ref-field-map "has_interest") collection))]

              [display-when-cta-warning-applicable collection
               [:div.error
                (debug "Details blank CTA fields" (map :ref blank-cta-fields) has-blank-cta-attrs)
                (when (and
                        (or (seq blank-cta-fields) has-blank-cta-attrs)
                        (not is-trust-with-no-interest))
                  [:div.o-media.error.u-bold cta-missing-fields-warning-text])]])
            (doall
              (for [[i segment] (map-indexed list filtered-segments)]
                ^{:key (:index segment)}
                [form-segment
                 (merge
                   {:attribute-column-counts attribute-column-counts
                    :max-attribute-column-count max-attribute-column-count
                    :form-fields form-fields
                    :cta-applies-fn (if (:field-class options) (fn [& _]) cta-applies?)
                    :ref-field-map ref-field-map
                    :entity-id-ref-field-map @entity-id-fields-map
                    :field-controls (:field-controls options)
                    :form-segment-class (:form-segment-class options)
                    :form-field-class (:form-field-class options)
                    :base-data (:base-data options)
                    :field-class (:field-class options)
                    :disable-collapsible (:disable-collapsible options)
                    :extra-content (some-> (:form-segment-header-extra-content options) (apply [(:index segment)]))}
                   (when (and
                           (zero? i)
                           first-segment-header-extra-content)
                     {:form-segment-header-extra-content first-segment-header-extra-content}))
                 segment collection]))]))])))

(defn form-segment [{:keys [form-segment-class
                            base-data]} segment collection]
  (let [collapsed? (atom (boolean (get-in collection [:data (keyword (str "ui_collapse_" (:ref segment)))])))]
    (fn [options segment collection]
      [:div.u-margin-bottom {:class form-segment-class}
       [:h3.u-margin-bottom.u-border-bottom.o-collapsable.u-flex.c-segment-header {:class (when @collapsed? "is-collapsed")}
        [:span.c-segment-label
         (:label segment)]
        (when-let [content (:form-segment-header-extra-content options)] content)
        (when-let [content (:extra-content options)] content)
        [:a.o-collapsable__expander {:on-click (util/handler #(swap! collapsed? not))}]]

       (when-not @collapsed?
         (let [filtered-fields (:filtered-fields segment)
               last-index (dec (count filtered-fields))]
           (doall (map-indexed (fn [index field]
                                 (let [col (model/field-collection field collection)
                                       field-opts (assoc options
                                                    :first (zero? index)
                                                    :last (= last-index index)
                                                    :base-value (get base-data (keyword (:ref field)))
                                                    :field-class (:field-class options)
                                                    :field-controls (:field-controls options)
                                                    :disable-collapsible (:disable-collapsible options))]
                                   ^{:key (:ref field)} [form-field field-opts field col]))
                    (for [field filtered-fields
                          :let [col (model/field-collection field collection)]
                          :when (and
                                  (not (contains? skip-fields (keyword (:type field))))
                                  (model/is-visible
                                    field
                                    (fn [aref]
                                      (get-in col [:data (keyword aref)]))
                                    :details)
)]
                      field)))))])))


(defn form-field [{:keys [expander collapsed-atom cta-applies-fn base-value
                          field-class]
                   :as options} field collection]
  (let [field-type (keyword (:type field))
        controls (some-> (:field-controls options) (apply [(:ref field)]))]
    [:div.field (merge
                 {:key (:ref field)}
                 {:class (apply util/classes
                           (concat
                             [(:form-field-class options)]
                             (when expander
                               [:o-collapsable :u-flex
                                (when @collapsed-atom :is-collapsed)])))})
     [:span
      (when-let [label (and
                         (not (hide-labels field-type))
                         (or
                           (:details_label field) ;; only ignore details_label if nil
                           (not-empty (:label field))))]
        ;; condition below: can be used to force no label for a field
        ;; by setting details_label to empty string
        (when (not-empty label)
          [:span.field-label label ": "]))
      (when-not (= "jurisdiction" (:type field)) controls)
      [:span {:class (apply util/classes
                       [(when (cta-applies-fn :highlight field) :u-color--highlighted)
                        field-class])}
       (if base-value
         (if (or
               (= base-value (get-value field collection))
               ;; TODO Implement showing current vs suggested for fields with objects.
               ;; Currently, at least single choice field, shows data based on `<ref>_objects` data item,
               ;; not based on IDs under ref and a dictionary of id-to-object. Hence it's now
               ;; not possible to show the current state, as `<ref>_objects` is populated with suggested
               ;; state
               (not-empty (:loader_ref field))
               ;; For document fields, the exclusion is not based on type:
               (= "document" (:type field)))
           [(field-types field-type string-field) options field collection]
           [:<>
            [:div [(field-types field-type string-field) options field (assoc-in collection [:data (keyword (:ref field))]
                                                                         base-value)] " (current)"]
            [:div [(field-types field-type string-field) options field collection] " (AARK suggestion)"]])
         [(field-types field-type string-field) options field collection])]]
     (when expander [:a.o-collapsable__expander {:on-click (util/handler #(swap! collapsed-atom not))}])]))

(defn string-field [{template :template
                     from-value :from-value}
                    field collection]
  [(if (:details_spread_out field) :div :span)
   (if (:editor field)
     [:div.u-background-color--turquoise.o-media--small.c-string-field (util/html-elem :p (util/html-break-text (get-value field collection)))]
     (let [value (if from-value
                   (from-value (get-value field collection))
                   (get-value field collection))]
       (if template
         (gstring/format template value)
         (cond
           (:email field) [:a {:href (str "mailto:" value)} value]
           (:phone field) [:a {:href (str "tel:" value)} value]
           :else value))))])

(defn number-field [{template :template
                     from-value :from-value
                     :as options} field collection]
  (when-not (:replicable field)
    [:span
     (let [value (if from-value
                   (from-value (get-value field collection))
                   (get-value field collection))]
       (if template
         (gstring/format template value)
         (str
          (when (:dollar field) "$")
          (util/format-number value)
          (when (:percent field) "%"))))]))

(defn date-field [options field collection]
  [:span (when-let [date (not-empty (get-value field collection))] (util/date-reformat date))])

(defn clicked-user-info-data [field item-formatter obj]
  (let [is-company? (= (:loader_ref field) "context_permitted_company_contacts")]
    (filter (fn [[_ v]] v)
            (conj
             (for [[user-field label]
                   (filterv identity 
                            [(when-not is-company? [:company_name "Company"])
                             [:title "Title/Role"]
                             [:middle_name "Middle Name"]
                             [:suffix "Suffix"]
                             [:phone_number "Phone Number"]
                             [:email "Email"]
                             [:physical_address_line_1 "Street Address"]
                             [:physical_address_line_2 "Suite/Unit Number"]
                             [:physical_address_city "City"]
                             [:physical_address_country "Country"]
                             [:physical_address_state "State"]
                             [:physical_address_zip "Zip Code"]
                             [:physical_address_type "Address Type"]
                             [:fin_cen_id "FinCEN ID"]])]
               [label (not-empty (user-field obj))])
             (when-let [formatted-name (not-empty (trim (item-formatter obj)))]
               ["Name" formatted-name])))))

(defn contact-icon [user has-cta?]
  (let [linked-data (:linked_data user)]
    (if (or (= "complete" linked-data) (= "incomplete" linked-data))
      [:span [:img {:src (str "/static/img/icons/group" (when has-cta? "-purple") ".svg") :width 12}] " "]
      [:span.info "ⓘ " ])))

(defn multi-choice-objects-field [options field collection]
  (let [is-user? ( = (:loader_ref field) "context_permitted_users")
        is-company? ( = (:loader_ref field) "context_permitted_company_contacts")
        item-formatter (cond
                         is-user? auth-model/get-full-name
                         is-company? #(:company_name %)
                         :else #(str "-"))
        objects (get-in collection [:data (keyword (str (:ref field) "_objects"))])
        percentage (get-in collection [:data (keyword (str (:ref field) "_percentage"))])
        collection-objects  (get-in collection [:data (keyword (:ref field))])
        details-spread-out (:details_spread_out field)
        clicked-user-info (atom nil)]

    (fn [options field collection]
      [(if details-spread-out :div :span)
       (doall 
         (for [obj-id collection-objects
               :let [idkey (keyword (str obj-id))
                     obj (get objects idkey)
                     is-contact? (:is_contact obj)]]

           ^{:key obj-id}
           [(if details-spread-out :div :span)
            [:a
             (when (and is-contact? (or is-user? is-company?))
               (do {:on-click (fn [event]
                                (do
                                  (reset! clicked-user-info
                                    (clicked-user-info-data field item-formatter obj))
                                  (infobox/info-container event @clicked-user-info)))
                    :style {:cursor "pointer"}}))

             (if is-contact?
               [:span (contact-icon obj (:cta field)) [:span (item-formatter obj)]]
               [:span (item-formatter obj)])

             (when (:percentage options)
               (str " (" (percentage idkey) "%)"))]
            (when (and
                    (not details-spread-out)
                    (not= (last collection-objects) obj-id ))
              [:span ", "])]))])))

(defn field-choices [field] (into {} (mapv (fn [[k v _]] [k v]) (filterv (fn [[_ _ valid]] valid) (:choices field)))))

(defn multi-choice-choices-field [_ field collection]
  (let [choices (field-choices field)
        choice-value-labels (for [value (get-value field collection)
                            :when (contains? choices value)]
                        [value (choices value)])]
    (if (:details_spread_out field)
      [:div
       (doall
         (for [[value choice-label] choice-value-labels]
           [:p {:key value} choice-label]))]
      [:span (clojure.string/join ", " (map second choice-value-labels))])))

(defn multi-choice-field [options field collection]
  (if (seq (:choices field))
    [multi-choice-choices-field options field collection]
    [multi-choice-objects-field options field collection]))

(defn multi-choice-percentage [options field collection]
  (let [is-user? ( = (:loader_ref field) "context_permitted_users")
        is-company? ( = (:loader_ref field) "context_permitted_company_contacts")
        item-formatter (or
                         (:item-formatter options)
                         (cond
                           is-user? auth-model/get-full-name
                           is-company? #(:company_name %)
                           :else #(str "-")))
        object-map (model/get-objects field collection)
        percentage (model/get-percentages field collection)
        percentage-visible (if-let [percentage-visibility (:percentage_visibility field)]
                             (model/is-visible
                               (assoc field
                                 :visibility percentage-visibility)
                               (fn [aref]
                                 (get-in collection [:data (keyword aref)])))
                             (not-empty (:unit_selector_field_ref field)))
        attributes (model/get-attributes field collection)
        objects (seq (map (fn [[idkey obj]]
                                  [idkey (assoc obj :attributes (get attributes idkey))]) object-map))
        clicked-user-info (atom nil)
        column-width (fn [main]
                       (field-column-width (inc (:max-attribute-column-count options)) main))
        ;; idkeys represent keys for for maps reurned by model/ get-objects get-percentage get-attributes
        ;; normally they correspond to numeric IDs, but for ownership they are tuples; the mapping function below
        ;; makes sure that the numeric IDs are keywords, which makes accessing maps returned by cljs-ajax easier
        make-idkey (fn [obj-id] (cond
                                  (vector? obj-id) obj-id
                                  (keyword? obj-id) obj-id
                                  :else (keyword (str obj-id))))
        idkeys (map make-idkey (model/get-value field collection))
        vattrs (visible-and-not-lonely-attrs field (:collection options) (fn [ref]
                                                                           (get-value ((:ref-field-map options) ref) collection)))
        sort-by-fn (fn [obj]
                     (conj
                      (vec (for [attr (map :ref (filter :sortable vattrs))]
                             (get (get (second obj) :attributes) (keyword attr))))
                      (model/object-compare-key obj)))]
    [:table {:style {:width "100%"}}
     [:tbody
      (when (seq objects)
        [:tr
         [:th {:style {:width (column-width true)}}]
         (when percentage-visible [:th {:style {:width (column-width false)}}
          [:span (:units-selected-label options)]])
         (doall
          (for [attr vattrs]
            [:th {:key (:ref attr)
                  :style {:width (column-width false)}} (:label attr)]))
         (doall (map-indexed (fn [index _]
                               [:th {:style {:width (column-width false)} :key index}])
                             (range (field-column-padding options field))))])
      
      (for [[obj-id obj] (sort-by sort-by-fn objects)
            :let [idkey (make-idkey obj-id)
                  attrs (attributes idkey)
                  is-contact? (:is_contact obj)
                  has-cta? (has-cta-attribute field attrs)]]
        [:tr (merge
               {:key obj-id}
               ;; CTA highlighting is set for the whole field higher in the hierarchy.
               ;; This disables highlighting for a row when CTA-related attribute does not
               ;; have required value (BOI = true)
               (when-not has-cta?
                 {:class :u-color-font}))
         [:td
          (when (and is-user? is-contact?)
            {:on-click (fn [event]
                         (reset! clicked-user-info
                                 (clicked-user-info-data field item-formatter obj))
                         (infobox/info-container event @clicked-user-info))
             :style {:cursor "pointer"}})

          (let [class (if has-cta? :u-color--highlighted :u-color-font)]
            (if is-contact?
              [:span {:class class} (contact-icon obj has-cta?) [:span (item-formatter obj)]]
              [:span {:class class} (item-formatter obj)]))]

         (when percentage-visible
           [:td.c-units (util/format-number-with-commas (percentage idkey)) (when-not (:has-units-selected options ) "%" )])
         (doall
          (for [attr vattrs]
            [:td {:key (:ref attr)} (attr-to-str attrs attr)]))
         (doall (map-indexed (fn [index _]
                               [:th {:style {:width (column-width false)} :key index}])
                  (range (field-column-padding options field))))])
      (when (and percentage-visible (seq objects))
        [:tr.c-total
         [:td "Total"]
         [:td.c-units
          (util/format-number-with-commas
            (dc/to-string (util/sum-decimals (map #(percentage %) idkeys))))
          (when-not (:has-units-selected options)  "%" )]])]]))

(defn attr-to-str [attrs attr]
  (let [value (get attrs (keyword (:ref attr)))
        object (get attrs (keyword (str (:ref attr) "_object")))
        choices (into {} (:choices attr))]
    (case (:type attr)
      "bool" (if value "Yes" "No")
      "single_choice" (cond
                        (map? object) (:name object)
                        (seq choices) (or (choices (str value)) (if (:allow_empty attr)
                                                                  ""
                                                                  (get-in attr [:choices 0 1])))
                        :else (str value))
      "single_choice_with_other" (let [choices (into {} (:choices (first (:subfields attr))))]
                                   (cond
                                     (map? object) (if (model/is-other-choice-value (first (:subfields attr)) (:id object))
                                                     (attrs (keyword (str (:ref attr) "_other")))
                                                     (:name object))
                                     (and (seq choices) (model/is-other-choice-value (first (:subfields attr)) value)) (attrs (keyword (str (:ref attr) "_other")))
                                     (seq choices) (or (choices (str value)) (if (:allow_empty attr)
                                                                               ""
                                                                               (str (get-in attr [:choices 0 1]))))
                                     :else (str value)))
      "string" value)))

(defn- field-column-width
  ([num-columns main]
   (let [main-column-width (condp = num-columns
                             1 100
                             2 70
                             3 50
                             33)]
     (if main
       (str main-column-width "%")
       (str (int ( / (- 100 main-column-width) (dec num-columns))) "%"))))
  ([num-columns] (field-column-width num-columns false)))

(defn- has-cta-attribute [field attr-ref-value-map]
  (boolean (seq (filter (fn [attr]
                          (if-let [cta-value (:cta_value attr)]
                            (and (:cta attr) (= cta-value (get attr-ref-value-map (keyword (:ref attr)))))
                            (:cta attr)))
                        (:attributes field)))))

(defn multi-choice-with-attrs-field [options field collection]
  (let [is-user? ( = (:loader_ref field) "context_permitted_users")
        is-company? ( = (:loader_ref field) "context_permitted_company_contacts")
        item-formatter (or
                         (:item-formatter options)
                         (cond
                           is-user? auth-model/get-full-name
                           is-company? #(:company_name %)
                           :else #(str "-")))
        object-map (model/get-objects field collection)
        objects (seq object-map)
        attributes (model/get-attributes field collection)
        visible-attributes (visible-and-not-lonely-attrs field collection (fn [ref]
                                      (get-value ((:ref-field-map options) ref) collection)))
        clicked-user-info (atom nil)
        column-width (fn [main]
                       (field-column-width (inc (:max-attribute-column-count options)) main))

        ;; idkeys represent keys for for maps returned by model/ get-objects get-percentage get-attributes
        ;; normally they correspond to numeric IDs, but for ownership they are tuples; the mapping function below
        ;; makes sure that the numeric IDs are keywords, which makes accessing maps returned by cljs-ajax easier
        make-idkey (fn [obj-id] (cond
                                  (vector? obj-id) obj-id
                                  (keyword? obj-id) obj-id
                                  :else (keyword (str obj-id))))]

    [:table {:style {:width "100%" :margin-bottom "1em"}}
     [:tbody
      [:tr [:th {:style {:width (column-width true)}}]
       (doall
         (for [attr visible-attributes]
           [:th {:key (:label attr)
                 :style {:width (column-width false)}} [:span (:label attr)]]))
       (doall (map-indexed (fn [index _]
                             [:th {:style {:width (column-width false)} :key index}])
                (range (field-column-padding options field))))]

      (for [[obj-id obj] (sort-by model/object-compare-key objects)
            :let [idkey (make-idkey obj-id)
                  attrs (get attributes idkey)
                  is-contact? (:is_contact obj)
                  has-cta? (has-cta-attribute field attrs)]]
        [:tr (merge
               {:key obj-id}
               ;; CTA highlighting is set for the whole field higher in the hierarchy.
               ;; This disables highlighting for a row when CTA-related attribute does not
               ;; have required value (BOI = true)
               (when-not has-cta?
                 {:class :u-color-font}))
         [:td
          (when (and is-user? is-contact?)
            {:on-click (fn [event]
                         (do
                           (reset! clicked-user-info
                                   (clicked-user-info-data field item-formatter obj))
                           (infobox/info-container event @clicked-user-info)))
             :style {:cursor "pointer"}})

          (if is-contact?
            [:span (contact-icon obj has-cta?)[:span (item-formatter obj)]]
            [:span (item-formatter obj)])]

         (doall
           (for [attr visible-attributes]
             [:td {:key (:ref attr)} (attr-to-str attrs attr)]))
         (doall (map-indexed (fn [index _]
                               [:th {:style {:width (column-width false)} :key index}])
                  (range (field-column-padding options field))))])]]))


(defn multi-choice-with-other-field [options field collection]
  (let [choice-field (first (filterv #(= (:type %) "multi_choice") (:subfields field)))
        other-field (first (filterv #(= (:type %) "string") (:subfields field)))
        choices (into {} (mapv (fn [[k v _]] [k v]) (filterv (fn [[_ _ valid]] valid) (:choices choice-field))))
        choice-labels (concat
                        (mapv #(choices %) (get-value choice-field collection))
                        (when-let [other-label (not-empty (get-value other-field collection))]
                          [other-label]))]
    [:span (clojure.string/join ", " choice-labels)]))

(defn single-choice-with-other-field [options field collection]
  (let [choice-field (first (filterv #(= (:type %) "single_choice") (:subfields field)))
        other-field (first (filterv #(= (:type %) "string") (:subfields field)))
        choices (into {} (mapv (fn [[k v _]] [k v]) (filterv (fn [[_ _ valid]] valid) (:choices choice-field))))]
    [:span
     (if (= "<OTHER>" (first (get-value choice-field collection)))
       (get-value other-field collection)
       (choices (first (get-value choice-field collection))))]))

(defn address-field [options field collection]
  (let [label-ref-value-triplets (filterv
                                   (comp not-empty #(nth % 2))
                                   (mapv (fn [[label subfield]]
                                           [label
                                            (:ref subfield)
                                            (if (= (:type subfield) "single_choice")
                                              (first (get-value subfield collection))
                                              (get-value subfield collection))])
                                     (map
                                       vector
                                       ["Street Address"
                                        "Suite/Unit Number"
                                        "City"
                                        "State"
                                        "Zip Code"
                                        "US or US Territory"]
                                       (:subfields field))))]
    [:div {:style (:address-field-style options)}
     (if (:details_spread_out field)
       (doall
         (for [[label ref value] label-ref-value-triplets]
           [:p {:key ref}
            [:b.u-color-font.u-font-size--smaller label ": "] value]))
       (clojure.string/join ", " (map (comp trim #(nth % 2)) label-ref-value-triplets)))]))

(defn intl-address-field [options field collection]
  (let [label-value-pairs (filterv (comp not-empty second) (mapv (fn [[label subfield]]
                                                             [label (if (= (:type subfield) "single_choice")
                                                               (first (get-value subfield (model/field-collection subfield collection)))
                                                               (get-value subfield (model/field-collection subfield collection)))])
                                                  (map
                                                    vector
                                                    ["Street Address"
                                                     "Suite/Unit Number"
                                                     "City"
                                                     "State"
                                                     "Province"
                                                     "Zip/Postal Code"
                                                     "Country"]
                                                    (:subfields field))))]
    [:div {:style (:address-field-style options)}
     (if (:details_spread_out field)
       (doall
         (for [[label value] label-value-pairs]
           [:p
            [:b.u-color-font.u-font-size--smaller label ": "] value]))
       (clojure.string/join ", " (map (comp trim second) label-value-pairs)))]))

(defn- document-field [options field collection]
  (let [documents (get-in collection [:data (keyword (str (:ref field) "_objects"))])
        document-ids (set (get-in collection [:data (keyword (:ref field))]))
        thumbnail-preview-id (atom nil)]
    [:ul.u-disc-list.document-field
     (doall
       (for [[k v] documents
             :when (contains? document-ids (:id v))]
         [:li {:key (:id v)}
          [document-thumbnail v thumbnail-preview-id (not (show-image-preview field v))]" "
          [:a.u-disc-list__newtab-button
           {:target "_blank"
            :href   (str "#/document/details/" (:id v))}
           [:img {:src "/static/img/icons/newtab.png"}]]
          (when (show-image-preview field v)
            [:div.c-document__preview
             [image-view "/download/documents/" (:download_key v) {:attachment :no}]])]))]))

(defn- label-field [options field collection] [:div.u-margin-top {:style {}} [:b (:label field) ": "]])

(defn- field-group [{:keys [collapsible]
                     :as options} field collection]
  (let [collapsible (if (:disable-collapsible options) false collapsible)
        collapsed? (atom collapsible)]
    (fn [options field collection]
      [:div {:class (apply util/classes [(when-not (:last options) :u-border-bottom)
                                         (when-not (:last options) :u-padding-bottom--xs)
                                         (when-not (:first options) :u-margin-top--xs)])}
       (let [[first-subfield & other-subfields] (filter-fields (:subfields field) collection)]
         [:div
          [form-field (assoc options
                        :expander collapsible
                        :collapsed-atom collapsed?) first-subfield (model/field-collection field collection)]
          (when-not @collapsed?
            (doall
              (for [subfield other-subfields]
                (let [field-type (keyword (:type subfield))]
                  [:div.field
                   {:key (:ref subfield)}
                   [form-field options subfield (model/field-collection subfield collection)]]))))])])))

(def inlineable-field-types
  #{:string
    :number
    :date
    :label
    :single_choice
    :multi_choice
    :multi_choice_with_other
    :foreign_tin
    :due_date_or_pair
    :month_day})

(defn inline-string-field-group [options field collection]
  [:span
   (let [filtered-fields (filter
                           (fn [subfield]
                             (and
                               (contains? inlineable-field-types (keyword (:type subfield)))
                               (model/is-visible subfield (fn [aref] (get-in collection [:data (keyword aref)])))
                               (has-nested-value subfield collection)))
                           (:subfields field))
         filtered-field-count (count filtered-fields)]
     (doall
       (map-indexed
         (fn [index subfield]
           [:span {:key (:ref subfield)}
            [(field-types (keyword (:type subfield)) string-field) options subfield collection]
            (when (< index (dec filtered-field-count))
              (or (:separator options) (:separator (:options field)) ", "))])
         filtered-fields)))])

(defn month-day-field [options {[month-field day-field] :subfields} collection]
  [:span
   [string-field (assoc options :from-value (comp str first)) month-field collection]
   "/"
   [string-field
    ;; Only needed because of #187315382. This commit fixes the value sent from edit form, so future entries
    ;; should be valid.
    ;; :from-value below is used to handle data alerady in the DB
    (assoc options :from-value #(or % 1))
    day-field collection]])

(defn format-clicked-collection-data [collection-data]
  (filterv identity 
           (conj
            [["Name" (:name collection-data)]]
            (when-let [fin-cen-id (get-in collection-data [:data :fincen_registration_id])]
              ["FinCEN ID" fin-cen-id]))))

(defn ownership-field [{:keys [form-fields] :as options} field collection]
  (let [form-fields-map (util/associate-by :ref form-fields)
        users-field-ref (:user_ownership_field_ref field)
        users-form-field (form-fields-map users-field-ref)
        units-selected (get-value (form-fields-map (:unit_selector_field_ref field)) collection)
        has-number-field (seq (:unit_selector_field_ref field)) ;; TODO: braindead
        ownership-collections (atom {})]
    ((if has-number-field multi-choice-percentage multi-choice-with-attrs-field)
     (merge options
            {:has-units-selected (not= ["percentage"] units-selected)
             :units-selected-label (cond
                                     (= ["units"] units-selected) "Units"
                                     (= ["shares"] units-selected) "Shares"
                                     :else "%")
             :item-formatter (fn [item]
                               (case (:item-type item)
                                 :user (auth-model/get-full-name item)
                                 :collection [:span
                                              [:a
                                               {:on-click (fn [event]
                                                                (let [owner-collection-id (get-in item [:owner_collection :id])]
                                                                  (if (contains? @ownership-collections owner-collection-id)
                                                                    (infobox/info-container event (format-clicked-collection-data (get @ownership-collections owner-collection-id)))
                                                                    (->> (collection-full-details owner-collection-id true)
                                                                         (p/map (fn [collection-data]
                                                                                  (swap! ownership-collections assoc (:id collection-data) collection-data)
                                                                                  (infobox/info-container event (format-clicked-collection-data collection-data))))
                                                                         (process-error)))))
                                                    :style {:cursor "pointer"}}
                                               [:span [:span.info "ⓘ "]]
                                              (if-let [asset-collection (get-in item [:asset_collection])]                                                
                                                [:a {:href (path-for :collection-details :id (:id asset-collection))}
                                                 (get-in item [:owner_collection :name])]
                                               (get-in item [:owner_collection :name]))]]
                                 "-"))})
     (assoc field
            :attributes (:attributes users-form-field)
            :loader_ref (:loader_ref users-form-field)) collection)))

(defn entity-field [_ field collection]
  (let [ownership (get-value {:ref (str (:ref field) "_ownership")} collection)]
    [:span [:a {:href (path-for :collection-details :id (:entity_id ownership))} (:entity_name ownership)]
     (when (seq (:units ownership))
      [:div
      [:b "Interest: "] (:percentage ownership) (if (= "percentage" (:units ownership)) "%" (str " " (:units ownership)))])]))

(defn field-to-value-fn [field]
  (fn [value]
    (case (:type field)
      :date (and value (util/local-date-time-to-date-string value))
      (str value))))

(defn bool_field [_ field collection]
  [:span (if (get-value field collection) "Yes" "No")])

(defn collection-single-field [_ field collection]
  (with-let [objects (get-in collection [:data (keyword (str (:ref field) "_objects"))])]
    (when-let [coll (some-> (vals objects) first :owner_collection)]
      [:span [:a {:href (path-for :collection-details :id (:id coll))} (:name coll)]])))

(def field-types
  {:string string-field
   :number number-field
   :date date-field
   :address address-field
   :intl_address intl-address-field
   :document document-field
   :multi_choice multi-choice-field
   :multi_choice_percentage multi-choice-percentage
   :percentage_with_units (fn [options field collection]
                            (let [units-selected (get-value (second (:subfields field)) collection)]
                              (multi-choice-percentage
                                (merge options
                                  {:has-units-selected (not= ["percentage"] units-selected)
                                   :units-selected-label (cond
                                                           (= ["units"] units-selected) "Units"
                                                           (= ["shares"] units-selected) "Shares"
                                                           :else "%")})
                                (first (:subfields field)) collection)))
   :ownership ownership-field
   :string_with_units (fn [_ field collection]
                        (string-field
                          {:template (if (= ["percentage"] (get-value (first (:subfields field)) collection)) "%s%%" "%s units")
                           :from-value (fn [value] (util/format-number value))}
                          (second (:subfields field)) collection))
   :multi_choice_with_other multi-choice-with-other-field
   :multi_choice_with_attrs multi-choice-with-attrs-field
   :single_choice multi-choice-field
   :bool bool_field 
   :single_choice_with_other single-choice-with-other-field
   :label label-field
   :jurisdiction (fn [options field collection]
                   (field-group (assoc options :collapsible true) field collection))
   :field_group field-group
   :inline_field_group inline-string-field-group
   :foreign_tin (fn [options field collection]
                  (inline-string-field-group
                    (assoc options :separator " ")
                    field collection))
   :month_day month-day-field
   :due_date_or_pair (fn [options field collection]
                       (inline-string-field-group
                         options
                         field collection))
   :collection (fn [& _] [:div])
   :collection_single collection-single-field
   :entity entity-field})


(def hide-labels #{:multi_choice_percentage :label})

(def skip-fields #{:compliance :export :collection})
